import React, { useState } from 'react';
import { Printer, Download, Activity, Heart, Brain, Stethoscope } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Header } from '../components/ui/Header';
import { InformedConsentModal } from '../components/InformedConsentModal';
import { PersonalDetails } from '../components/PersonalDetails';
import { PhysicalExamination } from '../components/PhysicalExamination';
import { FamilyHistory } from '../components/FamilyHistory';
import { LabInvestigations } from '../components/LabInvestigations';
import { DiagnosisTreatment } from '../components/DiagnosisTreatment';
import { Attachments } from '../components/Attachments';
import { useFormState } from '../hooks/useFormState';
export function MedicalForm() {
  const {
    formData,
    errors,
    handleChange,
    validate
  } = useFormState();
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      console.log('Form submitted:', formData);
    } else {
      alert('Please fill in all required fields.');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const handlePrint = () => {
    window.print();
  };
  const handleSavePDF = () => {
    window.print();
  };
  return <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50/30 to-amber-50 tcm-pattern print:bg-white print:p-0" data-id="element-681">
      <Header data-id="element-682" />

      <InformedConsentModal isOpen={isConsentModalOpen} onClose={() => setIsConsentModalOpen(false)} data-id="element-683" />

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 print:py-0 print:px-0" data-id="element-684">
        {/* Action Buttons - Hidden on Print */}
        <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center gap-4 mb-8 print:hidden" data-id="element-685">
          <div className="flex flex-wrap gap-2" data-id="element-686">
            <Button variant="outline" onClick={handlePrint} size="md" className="border-amber-600 text-amber-700 hover:bg-amber-50" data-id="element-687">
              <Printer className="w-4 h-4 mr-2" data-id="element-688" />
              Print
            </Button>
            <Button variant="outline" onClick={handleSavePDF} size="md" className="border-amber-600 text-amber-700 hover:bg-amber-50" data-id="element-689">
              <Download className="w-4 h-4 mr-2" data-id="element-690" />
              Save PDF
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8 print:space-y-4" data-id="element-691">
          {/* Single Column Layout for Better Flow */}
          <div className="space-y-8 print:space-y-4" data-id="element-692">
            {/* Section 1: Personal Details */}
            <div className="relative" data-id="element-693">
              <div className="bamboo-divider mb-6 print:hidden" data-id="element-694"></div>
              <PersonalDetails data={formData} onChange={handleChange} errors={errors} data-id="element-695" />
            </div>

            {/* Section 2: Physical Examination */}
            <div className="relative" data-id="element-696">
              <div className="bamboo-divider mb-6 print:hidden" data-id="element-697"></div>
              <PhysicalExamination data={formData} onChange={handleChange} data-id="element-698" />
            </div>

            {/* Section 3: Clinical Assessment - Two Column Grid */}
            <div className="relative" data-id="element-699">
              <div className="bamboo-divider mb-6 print:hidden" data-id="element-700"></div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:gap-4" data-id="element-701">
                <FamilyHistory data={formData} onChange={handleChange} data-id="element-702" />
                <LabInvestigations data={formData} onChange={handleChange} data-id="element-703" />
              </div>
            </div>

            {/* Section 4: Diagnosis, Treatment & Advice (Merged) */}
            <div className="relative" data-id="element-704">
              <div className="bamboo-divider mb-6 print:hidden" data-id="element-705"></div>
              <DiagnosisTreatment data={formData} onChange={handleChange} data-id="element-706" />
            </div>

            {/* Section 5: Attachments */}
            <div className="relative print:hidden" data-id="element-707">
              <div className="bamboo-divider mb-6" data-id="element-708"></div>
              <Attachments data={formData} onChange={handleChange} data-id="element-709" />
            </div>

            {/* Section 6: Informed Consent */}
            <div className="relative" data-id="element-710">
              <div className="bamboo-divider mb-6 print:hidden" data-id="element-711"></div>
              <div className="bg-white rounded-2xl border-2 border-amber-200 shadow-lg p-6 print:shadow-none print:rounded-lg print:p-4" data-id="element-712">
                <label className="flex items-start space-x-3 cursor-pointer" data-id="element-713">
                  <input type="checkbox" className="mt-1 rounded border-amber-400 text-amber-600 focus:ring-amber-500 w-5 h-5" checked={formData.consentAgreed} onChange={e => handleChange('consentAgreed', e.target.checked)} data-id="element-714" />
                  <span className="text-sm text-stone-700 leading-relaxed" data-id="element-715">
                    I have read and I agree to the{' '}
                    <button type="button" onClick={() => setIsConsentModalOpen(true)} className="text-blue-600 hover:text-blue-700 underline font-semibold print:hidden" data-id="element-716">
                      Informed Consent Form
                    </button>
                    <span className="hidden print:inline font-semibold" data-id="element-717">
                      Informed Consent Form
                    </span>
                  </span>
                </label>

                {/* Print and Save PDF Buttons */}
                <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-amber-200 print:hidden" data-id="element-718">
                  <Button type="button" variant="outline" onClick={handlePrint} className="border-amber-600 text-amber-700 hover:bg-amber-50" data-id="element-719">
                    <Printer className="w-4 h-4 mr-2" data-id="element-720" />
                    Print Form
                  </Button>
                  <Button type="button" variant="outline" onClick={handleSavePDF} className="border-amber-600 text-amber-700 hover:bg-amber-50" data-id="element-721">
                    <Download className="w-4 h-4 mr-2" data-id="element-722" />
                    Save as PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Services Section - Hidden on Print */}
        <div className="my-12 print:hidden" data-id="element-723">
          <div className="bamboo-divider mb-8" data-id="element-724"></div>

          {/* Our Services */}
          <div className="bg-gradient-to-br from-white to-amber-50/30 rounded-2xl shadow-xl border-2 border-amber-200 overflow-hidden" data-id="element-725">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-8 py-6" data-id="element-726">
              <h2 className="text-3xl font-bold text-white flex items-center gap-3" data-id="element-727">
                <Activity className="w-8 h-8" data-id="element-728" />
                Our Services
              </h2>
            </div>

            {/* Content Grid */}
            <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-729">
              {/* Column 1: Neurological, Head & Upper Body */}
              <div className="space-y-6" data-id="element-730">
                <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl p-6 border-2 border-amber-300" data-id="element-731">
                  <div className="flex items-center gap-3 mb-4" data-id="element-732">
                    <Brain className="w-6 h-6 text-amber-700" data-id="element-733" />
                    <h3 className="text-xl font-bold text-amber-900" data-id="element-734">
                      Neurological, Head & Upper Body Disorders
                    </h3>
                  </div>

                  <div className="space-y-4" data-id="element-735">
                    <div data-id="element-736">
                      <h4 className="font-bold text-amber-800 mb-2" data-id="element-737">
                        Neurological & Head Region
                      </h4>
                      <ul className="space-y-1 text-sm text-stone-700" data-id="element-738">
                        <li data-id="element-739">• Headache</li>
                        <li data-id="element-740">• Migraine</li>
                        <li data-id="element-741">• Facial Paralysis</li>
                        <li data-id="element-742">• Toothache</li>
                        <li data-id="element-743">• Tinnitus</li>
                        <li data-id="element-744">• Sore Eyes</li>
                      </ul>
                    </div>

                    <div data-id="element-745">
                      <h4 className="font-bold text-amber-800 mb-2" data-id="element-746">
                        Neck, Spine & Back
                      </h4>
                      <ul className="space-y-1 text-sm text-stone-700" data-id="element-747">
                        <li data-id="element-748">• Cervical Spondylosis</li>
                        <li data-id="element-749">• Neck Pain (Pharyngitis, Sore Throat)</li>
                        <li data-id="element-750">• Upper Back Pain</li>
                        <li data-id="element-751">
                          • Lower Back Pain (Sciatica, Lumbar Disc Herniation,
                          Pregnancy Back Pain)
                        </li>
                        <li data-id="element-752">• Back Ache</li>
                        <li data-id="element-753">• Slip Disc</li>
                      </ul>
                    </div>

                    <div data-id="element-754">
                      <h4 className="font-bold text-amber-800 mb-2" data-id="element-755">
                        Shoulder, Arm & Hand
                      </h4>
                      <ul className="space-y-1 text-sm text-stone-700" data-id="element-756">
                        <li data-id="element-757">
                          • Shoulder Pain (Frozen Shoulder, Arthritis, Trauma)
                        </li>
                        <li data-id="element-758">• Upper Arm Pain</li>
                        <li data-id="element-759">
                          • Elbow Pain (Tennis Elbow, Golf Elbow, Elbow
                          Arthritis)
                        </li>
                        <li data-id="element-760">• Forearm Pain</li>
                        <li data-id="element-761">• Wrist Pain</li>
                        <li data-id="element-762">• Finger Joint Pain</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 2: Chest, Abdomen & Lower Body */}
              <div className="space-y-6" data-id="element-763">
                <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl p-6 border-2 border-amber-300" data-id="element-764">
                  <div className="flex items-center gap-3 mb-4" data-id="element-765">
                    <Stethoscope className="w-6 h-6 text-amber-700" data-id="element-766" />
                    <h3 className="text-xl font-bold text-amber-900" data-id="element-767">
                      Chest, Abdomen & Lower Body Disorders
                    </h3>
                  </div>

                  <div className="space-y-4" data-id="element-768">
                    <div data-id="element-769">
                      <h4 className="font-bold text-amber-800 mb-2" data-id="element-770">
                        Chest & Rib Region
                      </h4>
                      <ul className="space-y-1 text-sm text-stone-700" data-id="element-771">
                        <li data-id="element-772">• Rib Pain</li>
                        <li data-id="element-773">• Shingles</li>
                      </ul>
                    </div>

                    <div data-id="element-774">
                      <h4 className="font-bold text-amber-800 mb-2" data-id="element-775">
                        Abdominal & Digestive Disorders
                      </h4>
                      <ul className="space-y-1 text-sm text-stone-700" data-id="element-776">
                        <li data-id="element-777">• Stomach Pain, Bloating</li>
                        <li data-id="element-778">
                          • Constipation, Acidity, Gas, Indigestion, Vomiting
                        </li>
                        <li data-id="element-779">• Nausea, Morning Sickness, Food Poisoning</li>
                        <li data-id="element-780">• Fatty Liver</li>
                        <li data-id="element-781">• Cholesterol</li>
                      </ul>
                    </div>

                    <div data-id="element-782">
                      <h4 className="font-bold text-amber-800 mb-2" data-id="element-783">
                        Lower Body & Joint Disorders
                      </h4>
                      <ul className="space-y-1 text-sm text-stone-700" data-id="element-784">
                        <li data-id="element-785">• Hip Pain (Hip Arthritis)</li>
                        <li data-id="element-786">• Buttock Pain</li>
                        <li data-id="element-787">• Thigh Pain</li>
                        <li data-id="element-788">
                          • Knee Pain (Knee Trauma, Arthritis, Meniscus Injury)
                        </li>
                        <li data-id="element-789">• Calf Pain</li>
                        <li data-id="element-790">• Ankle Pain (Sprain, Arthritis)</li>
                        <li data-id="element-791">• Foot Pain</li>
                        <li data-id="element-792">• Toe Joint Pain</li>
                        <li data-id="element-793">• Joint Pain</li>
                        <li data-id="element-794">• Arthritis</li>
                        <li data-id="element-795">• Uric Acid (Gout)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 3: Systemic, Immune & General Health */}
              <div className="space-y-6" data-id="element-796">
                <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl p-6 border-2 border-amber-300" data-id="element-797">
                  <div className="flex items-center gap-3 mb-4" data-id="element-798">
                    <Heart className="w-6 h-6 text-amber-700" data-id="element-799" />
                    <h3 className="text-xl font-bold text-amber-900" data-id="element-800">
                      Systemic, Immune & General Health Disorders
                    </h3>
                  </div>

                  <div className="space-y-4" data-id="element-801">
                    <div data-id="element-802">
                      <h4 className="font-bold text-amber-800 mb-2" data-id="element-803">
                        Systemic & Internal Diseases
                      </h4>
                      <ul className="space-y-1 text-sm text-stone-700" data-id="element-804">
                        <li data-id="element-805">• Diabetes</li>
                        <li data-id="element-806">• Blood Pressure</li>
                        <li data-id="element-807">• Thyroid</li>
                        <li data-id="element-808">• Obesity</li>
                        <li data-id="element-809">• Kidney Stones</li>
                        <li data-id="element-810">• Prostate Disorders</li>
                        <li data-id="element-811">• Bed Wetting</li>
                        <li data-id="element-812">• Paralysis</li>
                        <li data-id="element-813">• Numbness</li>
                        <li data-id="element-814">• Epilepsy</li>
                      </ul>
                    </div>

                    <div data-id="element-815">
                      <h4 className="font-bold text-amber-800 mb-2" data-id="element-816">
                        Respiratory & Immune Disorders
                      </h4>
                      <ul className="space-y-1 text-sm text-stone-700" data-id="element-817">
                        <li data-id="element-818">• Asthma</li>
                        <li data-id="element-819">• Allergies</li>
                        <li data-id="element-820">• Allergic Rhinitis</li>
                        <li data-id="element-821">• Sinusitis</li>
                        <li data-id="element-822">• Immunity Disorders</li>
                      </ul>
                    </div>

                    <div data-id="element-823">
                      <h4 className="font-bold text-amber-800 mb-2" data-id="element-824">
                        Reproductive, Mind & Growth
                      </h4>
                      <ul className="space-y-1 text-sm text-stone-700" data-id="element-825">
                        <li data-id="element-826">• Male / Female Diseases</li>
                        <li data-id="element-827">• Impotency / Infertility</li>
                        <li data-id="element-828">• Leucorrhoea</li>
                        <li data-id="element-829">• Depression</li>
                        <li data-id="element-830">• Insomnia</li>
                        <li data-id="element-831">• Eye Diseases</li>
                        <li data-id="element-832">• Height Increase</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Banner */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-8 py-6 text-center" data-id="element-833">
              <p className="text-xl font-bold text-white" data-id="element-834">
                All Conditions will be treated by Acupressure / Acupuncture /
                Reflexology
              </p>
            </div>
          </div>
        </div>

        {/* Enhanced Footer - Hidden on Print */}
        <footer className="mt-16 print:hidden" data-id="element-835">
          <div className="bamboo-divider mb-8" data-id="element-836"></div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl shadow-xl p-8 mb-6" data-id="element-837">
            <div className="max-w-4xl mx-auto" data-id="element-838">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-white" data-id="element-839">
                {/* WhatsApp Group */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20" data-id="element-840">
                  <div className="flex items-center gap-3 mb-3" data-id="element-841">
                    <h3 className="text-lg font-bold" data-id="element-842">
                      Join Our WhatsApp Group
                    </h3>
                  </div>
                  <a href="https://chat.whatsapp.com/CMmsr9Ozzgw01iSCC10ZMG?mode=hqrt1" target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-amber-700 px-6 py-3 rounded-lg font-semibold hover:bg-amber-50 transition-colors duration-200 mt-2" data-id="element-843">
                    Join Now →
                  </a>
                </div>

                {/* Course Inquiry */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20" data-id="element-844">
                  <div className="flex items-center gap-3 mb-3" data-id="element-845">
                    <h3 className="text-lg font-bold" data-id="element-846">Course Inquiry</h3>
                  </div>
                  <p className="text-amber-50 mb-2" data-id="element-847">Call or WhatsApp:</p>
                  <a href="tel:+918303699990" className="text-2xl font-bold hover:text-amber-100 transition-colors" data-id="element-848">
                    +91 8303699990
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright & Services */}
          <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200 p-8" data-id="element-849">
            <div className="text-center space-y-4" data-id="element-850">
              <p className="text-lg font-bold text-amber-900" data-id="element-851">
                © 2024 AATMSHAKTI Institute of Integrated Medical Research
              </p>
              <div className="h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent" data-id="element-852"></div>
              <p className="text-sm text-stone-600 leading-relaxed max-w-5xl mx-auto" data-id="element-853">
                <span className="font-semibold text-amber-800" data-id="element-854">
                  Confidential Patient Information
                </span>{' '}
                - Traditional Chinese Medicine | Acupuncture | Master Tung |
                Acupressure | Cosmetic Acupuncture | Infertility | Magnet
                Therapy | Reflexology | Weight Loss | Skin Treatment | Hair
                Treatment | and many More
              </p>
              <div className="pt-4" data-id="element-855">
                <p className="text-xs text-stone-500" data-id="element-856">
                  Plot No 41-42, Keshav Nagar, Lucknow U.P. Pin-226020
                </p>
              </div>
            </div>
          </div>
        </footer>

        {/* Print Footer - Only Visible on Print */}
        <div className="hidden print:block mt-8 pt-4 border-t-2 border-stone-300" data-id="element-857">
          <div className="flex justify-between text-xs text-stone-600 mb-6" data-id="element-858">
            <div data-id="element-859">
              <p className="font-semibold" data-id="element-860">Doctor's Signature:</p>
              <div className="border-b border-stone-400 w-48 mt-2 mb-1" data-id="element-861"></div>
              <p className="text-stone-500" data-id="element-862">Date: _______________</p>
            </div>
            <div className="text-right" data-id="element-863">
              <p className="font-semibold" data-id="element-864">Patient's Signature:</p>
              <div className="border-b border-stone-400 w-48 mt-2 mb-1" data-id="element-865"></div>
              <p className="text-stone-500" data-id="element-866">Date: _______________</p>
            </div>
          </div>

          <div className="text-center space-y-2" data-id="element-867">
            <p className="text-sm font-bold text-stone-800" data-id="element-868">
              © 2024 AATMSHAKTI Institute of Integrated Medical Research
            </p>
            <p className="text-xs text-stone-600" data-id="element-869">
              Plot No 41-42, Keshav Nagar, Lucknow U.P. Pin-226020 | Mob: +91
              8303699990, 9026625754
            </p>
            <p className="text-xs text-stone-500" data-id="element-870">
              Confidential Medical Record - Traditional Chinese Medicine &
              Acupuncture
            </p>
          </div>
        </div>
      </div>
    </div>;
}